import React from 'react';
import { Palette, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from '../../lib/i18n';
import { THEME_PALETTES } from '../../lib/themeSettings';
import { useThemeSettings } from '../../lib/ThemeContext';

const ThemeSettings: React.FC = () => {
  const { t } = useTranslation();
  const { colorTheme, setColorTheme } = useThemeSettings();

  const pick = (id: typeof colorTheme) => {
    setColorTheme(id);
    toast.success(t('settings.themeSaved'));
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-white p-3 sm:rounded-xl sm:p-5">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 sm:h-11 sm:w-11 sm:rounded-xl">
            <Palette className="h-4 w-4 sm:h-5 sm:w-5" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-zinc-900 sm:text-base">{t('settings.themeTitle')}</h3>
            <p className="mt-1 text-xs leading-relaxed text-zinc-600 sm:text-sm">{t('settings.themeHint')}</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 sm:rounded-xl sm:p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500 sm:text-xs sm:tracking-[0.12em]">
          {t('settings.themePreview')}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2 sm:mt-3 sm:gap-3">
          <span
            className="inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm sm:rounded-lg sm:px-4 sm:py-2 sm:text-sm"
            style={{ background: `linear-gradient(135deg, var(--theme-600), var(--theme-secondary-600))` }}
          >
            {t('common.save')}
          </span>
          <span className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-semibold text-zinc-800 sm:rounded-lg sm:px-3 sm:py-2 sm:text-sm">
            {t('nav.customers')}
          </span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-semibold sm:px-3 sm:py-1 sm:text-xs"
            style={{ backgroundColor: 'var(--theme-100)', color: 'var(--theme-800)' }}
          >
            {t('settings.themeActiveBadge')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        {THEME_PALETTES.map((opt) => {
          const on = colorTheme === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => pick(opt.id)}
              className={`group relative flex flex-col items-start overflow-hidden rounded-lg border p-2.5 text-left transition-all sm:rounded-xl sm:p-4 ${
                on
                  ? 'border-emerald-400 bg-emerald-50/80 ring-1 ring-emerald-200 sm:ring-2'
                  : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'
              }`}
            >
              <span
                className="mb-2 h-7 w-full rounded-md shadow-inner sm:mb-3 sm:h-10 sm:rounded-lg"
                style={{ background: `linear-gradient(135deg, ${opt.shades[500]}, ${opt.secondaryShades[600]})` }}
              />
              <span className="flex w-full items-center justify-between gap-1">
                <span className="text-[11px] font-semibold text-zinc-900 sm:text-sm">{t(opt.labelKey)}</span>
                {on ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 sm:h-5 sm:w-5" /> : null}
              </span>
              <span className="mt-1 flex gap-0.5 sm:gap-1">
                {[opt.shades[100], opt.shades[400], opt.shades[600], opt.shades[800]].map((c) => (
                  <span
                    key={c}
                    className="h-2 w-2 rounded-full ring-1 ring-zinc-200/80 sm:h-3 sm:w-3"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ThemeSettings;
